import React from 'react';
import type { Todo } from '@ext-bookmark/shared/types';
import type { Holiday } from '../../../services/todo/holidays';
import { getLunarDisplay } from '@ext-bookmark/shared/utils';
import { getDayTodoType } from '../shared/utils';

interface MobileDateCellProps {
  date: Date;
  todos: Todo[];
  isCurrentMonthDay: boolean;
  isTodayDay: boolean;
  isSelectedDay: boolean;
  holidays: Map<string, Holiday>;
  currentUserId: string | null;
  onDateClick: (date: Date) => void;
}

export const MobileDateCell: React.FC<MobileDateCellProps> = ({
  date,
  todos,
  isCurrentMonthDay,
  isTodayDay,
  isSelectedDay,
  holidays,
  currentUserId,
  onDateClick,
}) => {
  const dateStr = date.toISOString().split('T')[0];
  const holiday = holidays.get(dateStr);
  const todoType = getDayTodoType(todos, currentUserId);

  const indicatorColor = todoType === 'assigned' ? 'bg-amber-500 dark:bg-amber-400' : todoType === 'group' ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-primary';

  return (
    <div
      className={`
        aspect-square rounded-lg flex flex-col overflow-hidden min-h-0 cursor-pointer relative
        transition-all duration-200 ease-out
        ${!date ? 'invisible' : ''}
        bg-transparent
        ${isSelectedDay ? 'ring-2 ring-primary/40 bg-primary/10 ring-inset shadow-sm' : isTodayDay && !isSelectedDay ? 'bg-primary/5 dark:bg-primary/10 shadow-sm' : ''}
        hover:bg-black/[0.04] dark:hover:bg-white/[0.06] hover:shadow-sm
      `}
      style={{
        transition: `opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1)`
      }}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') {
          return;
        }
        if (target.closest('input[type="checkbox"]')) {
          return;
        }
        if (target.closest('button')) {
          return;
        }
        onDateClick(date);
      }}
      title="单击选中日期"
    >
      <div className="flex flex-col items-center justify-center h-full relative px-0.5 pb-1">
        {/* 日期数字 */}
        <div className={`
          text-lg font-bold
          ${isCurrentMonthDay ? '' : 'opacity-40'}
          ${isTodayDay || isSelectedDay ? 'text-primary' : 'text-foreground'}
        `}>
          {date.getDate()}
        </div>
        
        {/* 农历和节假日 */}
        <div className="text-[9px] text-gray-400 dark:text-gray-500 mt-0.5 min-h-[10px] leading-tight">
          {holiday && isCurrentMonthDay ? (
            <span className={`
              ${holiday.isWorkday && holiday.isOffDay
                ? 'text-amber-500 dark:text-amber-400'
                : holiday.isWorkday && !holiday.isOffDay
                ? 'text-amber-500 dark:text-amber-400'
                : 'text-rose-500 dark:text-rose-400'}
            `}>
              {holiday.isWorkday && !holiday.isOffDay ? '补' : holiday.name.slice(0, 2)}
            </span>
          ) : isCurrentMonthDay ? (
            getLunarDisplay(date)
          ) : null}
        </div>
        
        {/* 事项标识：小圆点（紧凑/非紧凑统一） */}
        {todos.length > 0 && isCurrentMonthDay && (
          <div className="mt-0.5">
            <div className={`w-1.5 h-1.5 rounded-full ${indicatorColor} shadow-sm`} />
          </div>
        )}
      </div>
    </div>
  );
};
